import type {
  Domain,
  DomainStatus,
  ProductStatus,
  ShopperHiddenReason,
  TenantStatus,
} from '@avrahami/contracts';
import { Badge, formatDate, StatusBadge, type BadgeTone } from '@avrahami/ui';
import { he } from '../i18n/he';
import type { CopyRowState } from '../lib/copy-types';
import { certificateState, domainChip } from '../lib/domains';

const tones = {
  PROVISIONING: 'blue',
  ACTIVE: 'green',
  SUSPENDED: 'amber',
  CANCELLED: 'red',
  CLOSED: 'slate',
} as const satisfies Record<TenantStatus, BadgeTone>;

const domainTones = {
  SYSTEM: 'slate',
  PENDING: 'amber',
  VERIFYING: 'blue',
  ACTIVE: 'green',
  FAILED: 'red',
  DISABLED: 'slate',
  DNS_LOST: 'red',
} as const satisfies Record<DomainStatus | 'SYSTEM', BadgeTone>;

const productTones = {
  DRAFT: 'slate',
  ACTIVE: 'green',
  HIDDEN: 'amber',
  ARCHIVED: 'red',
} as const satisfies Record<ProductStatus, BadgeTone>;

const copyRowTones = {
  IN_PROGRESS: 'blue',
  FAILED: 'red',
  WITHOUT_COPY: 'slate',
  GENERATED: 'amber',
  APPROVED: 'green',
  EDITED: 'green',
} as const satisfies Record<CopyRowState, BadgeTone>;

export function TenantStatusBadge({ status }: { status: TenantStatus }) {
  return (
    <StatusBadge value={status} tones={tones} labels={he.tenantStatus} testId="tenant-status" />
  );
}

/**
 * The state of a store's product, on the copy screen. An ACTIVE product that shoppers do not see
 * (adr/0046) says why instead of "shown in the store".
 */
export function ProductStatusBadge({
  status,
  hiddenFromShoppers = null,
}: {
  status: ProductStatus;
  hiddenFromShoppers?: ShopperHiddenReason | null;
}) {
  if (status === 'ACTIVE' && hiddenFromShoppers !== null) {
    return (
      <span data-testid="product-status" data-status={hiddenFromShoppers}>
        <Badge tone="amber">{he.shopperHidden[hiddenFromShoppers]}</Badge>
      </span>
    );
  }
  return (
    <StatusBadge
      value={status}
      tones={productTones}
      labels={he.productStatus}
      testId="product-status"
    />
  );
}

/** The state of a product's marketing copy, on the copy screen. */
export function CopyStateBadge({ state }: { state: CopyRowState }) {
  return (
    <StatusBadge
      value={state}
      tones={copyRowTones}
      labels={he.aiCopy.rowState}
      testId="copy-state"
    />
  );
}

/** An address of a store: the state of an own domain, or that it is the system address. */
export function DomainStatusBadge({ domain }: { domain: Pick<Domain, 'kind' | 'status'> }) {
  const chip = domainChip(domain);
  return (
    <span data-testid="domain-status" data-status={domain.status} data-kind={domain.kind}>
      <Badge tone={domainTones[chip]}>{he.domains.status[chip]}</Badge>
    </span>
  );
}

/**
 * The end of a domain's certificate, marked in words when it is near or past (`now` is when the
 * data was read, so the mark does not depend on when the screen happens to render).
 */
export function CertificateDate({ validUntil, now }: { validUntil: string | null; now: number }) {
  const state = certificateState(validUntil, now);
  if (validUntil === null || state === 'NONE') {
    return (
      <span data-testid="domain-certificate" data-state="NONE">
        {he.common.none}
      </span>
    );
  }
  return (
    <span
      data-testid="domain-certificate"
      data-state={state}
      className="inline-flex flex-wrap items-center gap-2"
    >
      <span className={state === 'OK' ? 'text-xs text-slate-600' : 'text-xs font-semibold'}>
        {formatDate(validUntil)}
      </span>
      {state === 'OK' ? null : (
        <Badge tone={state === 'ENDED' ? 'red' : 'amber'}>{he.domains.certificate[state]}</Badge>
      )}
    </span>
  );
}
